import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable } from 'rxjs'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { FacilitySelectionBehaviour, LocalSettings } from './local-settings'
import { WebLocalStorage, WebSessionStorage } from '@elderbyte/ngx-starter'
import { SessionSettings } from './session-settings'

@Injectable({
  providedIn: 'root',
})
export class SettingsStorageService {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)

  private readonly SETTINGS_KEY = 'mexusSettings'

  private _sessionSettings$: BehaviorSubject<SessionSettings> =
    new BehaviorSubject<SessionSettings>(SessionSettings.EMPTY())
  private _localSettings$: BehaviorSubject<LocalSettings> = new BehaviorSubject<LocalSettings>(
    LocalSettings.EMPTY()
  )

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor(
    private sessionStorage: WebSessionStorage,
    private localStorage: WebLocalStorage
  ) {
    this._sessionSettings$.next(this.loadFromSessionStorage())
    this._localSettings$.next(this.loadFromLocalStorage())
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public getCurrentFacility(): string | null {
    // Session
    const currentFacility = this.currentFacility
    if (currentFacility) {
      return currentFacility
    }

    // Local
    const facilitySelection = this.defaultFacilitySelection
    if (facilitySelection === FacilitySelectionBehaviour.USER_DEFINED) {
      const preferredFacility = this.userPreferredFacility
      if (preferredFacility) {
        return preferredFacility
      }
    }

    return this.lastUsedFacility
  }

  public setCurrentFacility(facilityId: string): void {
    this.currentFacility = facilityId // Session storage
    this.lastUsedFacility = facilityId // Local storage
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  public get localSettings$(): Observable<LocalSettings> {
    return this._localSettings$.asObservable()
  }

  public get localSettings(): LocalSettings {
    return this._localSettings$.getValue()
  }

  public set localSettings(settings: LocalSettings) {
    this.saveToLocalStorage(settings)
  }

  public get defaultFacilitySelection(): FacilitySelectionBehaviour {
    return this.localSettings.facilitySelectionBehaviour
  }

  public set defaultFacilitySelection(facilitySelection: FacilitySelectionBehaviour) {
    if (facilitySelection) {
      const settings = this.loadFromLocalStorage()
      settings.facilitySelectionBehaviour = facilitySelection
      this.saveToLocalStorage(settings)
    }
  }

  public get userPreferredFacility(): string {
    return this.localSettings.userPreferredFacility
  }

  public set userPreferredFacility(facility: string) {
    if (facility) {
      const settings = this.loadFromLocalStorage()
      settings.userPreferredFacility = facility
      this.saveToLocalStorage(settings)
    }
  }

  public get lastUsedFacility(): string {
    return this.localSettings.lastUsedFacility
  }

  public set lastUsedFacility(facility: string) {
    if (facility) {
      const settings = this.loadFromLocalStorage()
      settings.lastUsedFacility = facility
      this.saveToLocalStorage(settings)
    }
  }

  public get sessionSettings(): SessionSettings {
    return this._sessionSettings$.getValue()
  }

  public set sessionSettings(settings: SessionSettings) {
    this.saveToSessionStorage(settings)
  }

  public get currentFacility(): string {
    return this.sessionSettings.currentFacility
  }

  public set currentFacility(facility: string) {
    if (facility) {
      const settings = this.loadFromSessionStorage()
      settings.currentFacility = facility
      this.saveToSessionStorage(settings)
    }
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private loadFromLocalStorage(): LocalSettings {
    const settingsRaw = this.localStorage.getItem(this.SETTINGS_KEY)
    return settingsRaw ? JSON.parse(settingsRaw) : LocalSettings.EMPTY()
  }

  private loadFromSessionStorage(): SessionSettings {
    const settingsRaw = this.sessionStorage.getItem(this.SETTINGS_KEY)
    return settingsRaw ? JSON.parse(settingsRaw) : SessionSettings.EMPTY()
  }

  private saveToLocalStorage(settings: LocalSettings): void {
    this.log.debug('Storing settings in local storage:', settings)
    this.localStorage.setItem(this.SETTINGS_KEY, JSON.stringify(settings))
    this._localSettings$.next(settings)
  }

  private saveToSessionStorage(settings: SessionSettings): void {
    this.log.debug('Storing settings in session storage:', settings)
    this.sessionStorage.setItem(this.SETTINGS_KEY, JSON.stringify(settings))
    this._sessionSettings$.next(settings)
  }
}
